import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { identifyUser, resetAnalytics } from "@/lib/analytics";
import { safeLocalStorage } from "@/lib/safeStorage";
import { resetProductionPrefetchState } from "@/hooks/useProductionPagePrefetch";
import { isAuthSessionError } from "@/lib/authSessionError";
import { withTimeout } from "@/lib/withTimeout";
import {
  BackendUnavailableError,
  isAuthDataUnknown,
  type QueryAvailability,
} from "@/lib/backendAvailability";
import { endExpiredSession } from "@/lib/endExpiredSession";
import * as Sentry from "@sentry/react";
import { toSentryUser } from "@produce/shared";

export type UserRole = "admin" | "producer" | "crew";

/**
 * How long a single subscription lookup may take before we call the backend
 * unreachable. Long enough for a slow phone connection, short enough that a
 * dead database shows a retry screen instead of an endless spinner.
 */
const SUBSCRIPTION_TIMEOUT_MS = 12_000;

interface SubscriptionStatus {
  subscribed: boolean;
  plan_type: "monthly" | "yearly" | "lifetime_free" | "comp" | "manual" | null;
  plan_tier: "lite" | "pro" | "studio" | null;
  plan_status?: "trial" | "active" | "canceled" | null;
  subscription_end: string | null;
  trial_ends_at?: string | null;
  manual_producer_access?: boolean;
  price?: number | null;
  discount_info?: {
    code: string;
    type: string;
    value?: number;
    duration_type?: string;
    duration_months?: number;
    end_date?: string | null;
  } | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userId: string | null;
  roles: UserRole[];
  isAdmin: boolean;
  isProducer: boolean;
  isCrew: boolean;
  subscriptionStatus: SubscriptionStatus | null;
  hasProducerAccess: boolean;
  /** Briefing ids this user is an active (non-revoked) co-producer of. */
  coProducerBriefingIds: string[];
  /** Has co-producer access to at least one production. */
  isCoProducer: boolean;
  /** A co-producer who is NOT also a full producer/admin (link-only account). */
  isCoProducerOnly: boolean;
  isLoading: boolean;
  isRolesLoading: boolean;
  isSubscriptionLoading: boolean;
  /**
   * The backend could not be reached for at least one of the answers a route
   * guard needs (roles, co-producer memberships, subscription) and nothing is
   * cached. Guards show a retry screen rather than assume "no access".
   */
  isAuthDataUnavailable: boolean;
  /** The co-producer-memberships query is still resolving. */
  isCoProducerLoading: boolean;
  refreshSubscription: () => Promise<void>;
  refreshRoles: () => Promise<void>;
  /** Re-runs every answer the route guards depend on. */
  refreshAuthData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const userId = user?.id ?? null;

  // ── User Roles ──────────────────────────────────────────────────────
  const {
    data: rolesData,
    refetch: refetchRoles,
    isLoading: isRolesLoading,
    isError: isRolesError,
    fetchStatus: rolesFetchStatus,
  } = useQuery({
    queryKey: ["user-roles", userId],
    queryFn: async (): Promise<UserRole[]> => {
      if (!userId) return [];
      const { data, error } = await withTimeout(
        Promise.resolve(supabase.from("user_roles").select("role").eq("user_id", userId)),
        SUBSCRIPTION_TIMEOUT_MS,
        "user_roles lookup",
      );
      // PostgREST resolves with an error rather than throwing, so without this
      // an unreachable database looked like "this user has no roles" — and a
      // producer stayed "not a producer" even after it came back.
      if (error) throw new BackendUnavailableError("your account roles");
      return (data?.map((r) => r.role) || []) as UserRole[];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes — roles rarely change
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
  const roles = rolesData ?? [];

  // ── Co-producer memberships ─────────────────────────────────────────
  // Which productions (briefings) this user can access as a co-producer.
  // RLS ("Co-producers can view their own row") scopes this to their own
  // rows. Drives the route guards so a co-producer stays inside one
  // production and never reaches the overview / settings / my-team layer.
  const {
    data: coProducerData,
    refetch: refetchCoProducer,
    isLoading: isCoProducerLoading,
    isError: isCoProducerError,
    fetchStatus: coProducerFetchStatus,
  } = useQuery({
    queryKey: ["co-producer-briefings", userId],
    queryFn: async (): Promise<string[]> => {
      if (!userId) return [];
      const { data, error } = await withTimeout(
        Promise.resolve(
          supabase
            .from("production_co_producers")
            .select("briefing_id")
            .eq("co_producer_user_id", userId)
            .is("revoked_at", null),
        ),
        SUBSCRIPTION_TIMEOUT_MS,
        "co-producer lookup",
      );
      if (error) throw new BackendUnavailableError("your production access");
      return (data?.map((r) => r.briefing_id) || []) as string[];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
  const coProducerBriefingIds = coProducerData ?? [];

  // ── Subscription Status ─────────────────────────────────────────────
  const {
    data: subscriptionStatus,
    refetch: refetchSubscription,
    isLoading: isSubscriptionQueryLoading,
    isError: isSubscriptionError,
    fetchStatus: subscriptionFetchStatus,
  } = useQuery({
    queryKey: ["subscription-status", userId],
    queryFn: async () => {
      // Always get fresh session to avoid using expired tokens
      // Not just a cache read: supabase-js refreshes an expired token here,
      // which is a network call — and the one that hung longest in the outage.
      const {
        data: { session: currentSession },
      } = await withTimeout(supabase.auth.getSession(), SUBSCRIPTION_TIMEOUT_MS, "session refresh");

      if (!currentSession?.access_token || !userId) {
        return null;
      }

      try {
        const { data, error } = await withTimeout(
          supabase.functions.invoke("check-subscription"),
          SUBSCRIPTION_TIMEOUT_MS,
          "check-subscription",
        );
        if (!error && data) {
          return data as SubscriptionStatus;
        }
        // A revoked session (e.g. right after a password change) makes this
        // call 401 while PostgREST may still accept the same token. Without
        // this branch the fallback below would resolve to "not subscribed"
        // and the route guard would strand the user on /upgrade.
        if (error && isAuthSessionError(error)) {
          await endExpiredSession();
          return null;
        }
      } catch (error) {
        if (isAuthSessionError(error)) {
          await endExpiredSession();
          return null;
        }
        // Edge function unavailable, fall back to direct DB check
      }

      // Fallback: check database directly
      try {
        const { data: profile, error: profileError } = await withTimeout(
          // PostgREST builders are thenable but not Promises — adopt first.
          Promise.resolve(
            supabase.from("profiles").select("manual_producer_access").eq("id", userId).single(),
          ),
          SUBSCRIPTION_TIMEOUT_MS,
          "profiles lookup",
        );

        // Once PostgREST rejects the token too, the session is definitively
        // dead — sign out rather than reporting "no access".
        if (profileError && isAuthSessionError(profileError)) {
          await endExpiredSession();
          return null;
        }

        if (profile?.manual_producer_access) {
          return {
            subscribed: true,
            plan_status: "active" as SubscriptionStatus["plan_status"],
            plan_tier: "studio" as SubscriptionStatus["plan_tier"],
            plan_type: "lifetime_free" as SubscriptionStatus["plan_type"],
            subscription_end: null,
            manual_producer_access: true,
          };
        }

        const { data: dbSub, error: dbError } = await withTimeout(
          Promise.resolve(
            supabase
              .from("producer_subscriptions")
              .select("plan_status, plan_tier, trial_ends_at, subscription_end")
              .eq("user_id", userId)
              .maybeSingle(),
          ),
          SUBSCRIPTION_TIMEOUT_MS,
          "producer_subscriptions lookup",
        );

        if (dbError && isAuthSessionError(dbError)) {
          await endExpiredSession();
          return null;
        }
        // Could not read the row at all — "unknown", not "not subscribed".
        if (dbError) throw new BackendUnavailableError("your subscription");
        if (!dbSub)
          return { subscribed: false, plan_type: null, plan_tier: null, subscription_end: null };

        const isActive =
          (dbSub.plan_status === "active" ||
            (dbSub.plan_status === "trial" &&
              dbSub.trial_ends_at &&
              new Date(dbSub.trial_ends_at) > new Date())) &&
          !!dbSub.plan_tier; // Must have chosen a tier

        return {
          subscribed: isActive,
          plan_status: dbSub.plan_status as SubscriptionStatus["plan_status"],
          plan_tier: dbSub.plan_tier as SubscriptionStatus["plan_tier"],
          plan_type: null,
          subscription_end: dbSub.subscription_end,
          trial_ends_at: dbSub.trial_ends_at,
          manual_producer_access: false,
        };
      } catch (error) {
        // Both the edge function and the direct read failed: the backend is
        // unreachable. Surfacing that as an error keeps the route guard from
        // concluding "no access" and stranding a paying producer on /upgrade.
        if (error instanceof BackendUnavailableError) throw error;
        throw new BackendUnavailableError("your subscription");
      }
    },
    enabled: !!session?.access_token && !!userId,
    staleTime: 30_000, // 30 seconds — shared across components
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (was 0!)
    refetchOnMount: "always",
    refetchInterval: (query) => {
      if (!session?.access_token) return false;
      // Keep trying while the answer is unknown, so the app heals itself when
      // the backend comes back instead of waiting for the user to press retry.
      return query.state.data !== undefined ? 60_000 : 15_000;
    },
    retry: false,
  });

  // ── Availability ────────────────────────────────────────────────────
  // A guard needs three answers (roles, memberships, subscription). If any of
  // them is unknown AND uncached, it must say so instead of guessing — see
  // lib/backendAvailability.
  const availability: QueryAvailability[] = [
    {
      isError: isRolesError,
      isPaused: rolesFetchStatus === "paused",
      hasData: rolesData !== undefined,
    },
    {
      isError: isCoProducerError,
      isPaused: coProducerFetchStatus === "paused",
      hasData: coProducerData !== undefined,
    },
    {
      isError: isSubscriptionError,
      isPaused: subscriptionFetchStatus === "paused",
      hasData: subscriptionStatus !== undefined,
    },
  ];
  const isAuthDataUnavailable = !!userId && isAuthDataUnknown(availability);

  // ── Computed Properties ─────────────────────────────────────────────
  const isAdmin = roles.includes("admin");
  const isProducer = roles.includes("producer");
  const isCrew = roles.includes("crew");

  const hasProducerAccess = useMemo(() => {
    if (isAdmin) return true;
    if (!isProducer) return false;
    if (subscriptionStatus?.manual_producer_access) return true;
    if (!subscriptionStatus) return false;
    return subscriptionStatus.subscribed === true;
  }, [isAdmin, isProducer, subscriptionStatus]);

  const isCoProducer = coProducerBriefingIds.length > 0;
  const isCoProducerOnly = isCoProducer && !isProducer && !isAdmin;

  // Tag Sentry events with the signed-in account.
  //
  // Without this every error was anonymous, so "did this producer hit an
  // error?" — the question the admin panel now links out to — had no answer at
  // all. Signing out clears it, so the next person on a shared machine does not
  // inherit the previous one's identity.
  useEffect(() => {
    Sentry.setUser(toSentryUser(user));
  }, [user]);

  // ── Auth State Listener ─────────────────────────────────────────────
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (event === "SIGNED_OUT") {
        queryClient.cancelQueries();
        queryClient.removeQueries();
        queryClient.clear();
        // Clearing the cache only stops what is already in flight. The portal's
        // background page-warm walks several pages over several seconds; left
        // running it would keep reading as a signed-out client and cache RLS's
        // empty answers as fresh results for the next producer to sign in.
        resetProductionPrefetchState();
        safeLocalStorage.removeItem("produce_cached_auth_session");
        safeLocalStorage.removeItem("produce_cached_user_session");
        resetAnalytics();
      }

      if (newSession?.user && event === "SIGNED_IN") {
        identifyUser(newSession.user.id, { email: newSession.user.email });
      }

      if (newSession?.user && event !== "TOKEN_REFRESHED") {
        // Defer refetch to avoid calling inside the auth state change callback
        // Skip TOKEN_REFRESHED to prevent Safari bfcache causing visible re-renders
        setTimeout(() => {
          refetchRoles();
          refetchSubscription();
        }, 0);
      }
    });

    // Check for existing session
    supabase.auth
      .getSession()
      .then(({ data: { session: existingSession } }) => {
        setSession(existingSession);
        setUser(existingSession?.user ?? null);
        setIsLoading(false);

        if (existingSession?.user) {
          safeLocalStorage.setItem(
            "produce_cached_auth_session",
            JSON.stringify({
              user: { id: existingSession.user.id, role: existingSession.user.role },
              timestamp: Date.now(),
            }),
          );
        }
      })
      .catch(() => {
        if (!navigator.onLine) {
          const cached = safeLocalStorage.getItem("produce_cached_auth_session");
          if (cached) {
            try {
              const { user: cachedUser, timestamp } = JSON.parse(cached);
              if (timestamp && Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) {
                setUser(cachedUser);
              }
            } catch {
              // Failed to parse
            }
          }
        }
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSubscriptionFn = async () => {
    await refetchSubscription();
    queryClient.invalidateQueries({ queryKey: ["user-session"] });
  };

  // Everything the route guards depend on. Refreshing only the subscription
  // left a bogus empty roles list cached, so "try again" landed the user on
  // /upgrade anyway.
  const refreshAuthDataFn = async () => {
    await Promise.all([refetchRoles(), refetchCoProducer(), refetchSubscription()]);
  };

  const refreshRolesFn = async () => {
    await refetchRoles();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userId,
        roles,
        isAdmin,
        isProducer,
        isCrew,
        subscriptionStatus: (subscriptionStatus as SubscriptionStatus) ?? null,
        hasProducerAccess,
        coProducerBriefingIds,
        isCoProducer,
        isCoProducerOnly,
        isLoading,
        isRolesLoading,
        isSubscriptionLoading: isSubscriptionQueryLoading,
        isAuthDataUnavailable,
        isCoProducerLoading,
        refreshSubscription: refreshSubscriptionFn,
        refreshRoles: refreshRolesFn,
        refreshAuthData: refreshAuthDataFn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
